/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faAtlas,
  faCheck,
  faCheckCircle,
  faCircleExclamation,
  faEdit,
  faEye,
  faTimesCircle,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import useAction from '../../../hooks/useAction'
import { usePermissions } from '../../../hooks/usePermissions'
import useNotification from '../../../hooks/useNotification'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import { formatDateToInput } from '../../../utils/formatDateToInput'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import ListHeader from '../../../components/ListHeader'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'
import { genericStatusColorMap } from '../../../utils/statusColorMap'
import {
  GenericStatusEnum,
  GenericStatusEnumById,
} from '../../../enums/generic-status-enum'
import OrderEnum, {
  getOrderSituationLabel,
  OrderSituationEnum,
} from '../../../enums/order-enum'
import MovementEnum from '../../../enums/movement-enum'
import { invalidateByEvent } from '../../../utils/query-invalidator'
import { EquipmentEnum } from '../../../enums/equipment-enum'
import { Box, Chip, IconButton } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import InputSelect from '../../../components/InputSelect'
import ServiceOrderDetailsPanel from './components/ServiceOrderDetailsPanel'

const OrdersServicesPage = () => {
  const { canAccess, userRoles = [] } = usePermissions()
  const canDecideExchange = userRoles.some(({ name }) =>
    ['Gestor', 'Admin'].includes(name)
  )
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const {
    get: baseServiceGet,
    put: baseServicePut,
    del: baseServiceDelete,
  } = useBaseService(navigate)

  const queryParams = new URLSearchParams(location.search)

  const contractId = queryParams.get('contract_id')

  const { selectedOrgan } = useOrgan()

  // Requisição atualmente expandida na listagem (apenas uma por vez).
  // O painel de detalhes só é montado/buscado quando este ID está preenchido.
  const [expandedOrderId, setExpandedOrderId] = useState(null)

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      'contract.number': [],
      'recipientCustomer.name': [],
      'destination_address.alias': [],
      'status.name': [],
      'items.equipment.number': [],
      'movementType.name': [],
      organization_id: selectedOrgan?.id,
      situation: [],
      deleted_at: false,
    },
    'orders'
  )

  // Fecha o painel de detalhes quando a listagem é filtrada ou paginada.
  useEffect(() => {
    setExpandedOrderId(null)
  }, [filters, pagination.page, pagination.perPage])

  const FILTER_FIELD_CONFIG = {
    'items.equipment.number': 'value', // AutoCompleteFilter com valueKey="number" → extrai .value
  }

  const { handleFilterApply, handleFilterClear } = useFilterHandlers(
    applyUrlFilters,
    FILTER_FIELD_CONFIG
  )

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        'contract.number': [],
        'recipientCustomer.name': [],
        'destination_address.alias': [],
        'status.name': [],
        'items.equipment.number': [],
        'movementType.name': [],
        organization_id: selectedOrgan?.id,
        situation: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const {
    data: ordersResponseData,
    isLoading: isLoadingOrders,
    isFetching: isFetchingOrders,
    isError: isErrorOrders,
    error: ordersQueryError,
  } = useQueryWithLoader({
    queryKey: ['orders', filters, pagination],
    queryFn: async () => {
      showLoader()
      const filtersTranformed = transformFiltersForUrl({
        ...filters,
        ...(contractId && { 'contract.id': contractId }),
      })
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.serviceOrders.list, params)
      return response.result
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true,
    select: (data) => {
      return {
        orders: data?.data?.map((item) => ({
          id: item?.id,
          contract_id: item?.contract_id,
          contract_number: item?.contract?.number || '-',
          contract: item?.contract?.name || '-',
          recipient_customer: item?.recipient_customer?.alias || '-',
          destination_order: item?.destination_address?.alias || '-',
          statusOs: GenericStatusEnumById[item?.status_id] || '-',
          deadline: formatDateToInput(item?.deadline) || '-',
          type: item.movement_type?.name || '-',
          type_id: item.movement_type?.id,
          status: !item?.deleted_at ? 'Ativo' : 'Inativo',
          status_id: item?.status_id,
          organization: item?.organization?.alias || '-',
          has_exchange:
            item?.has_exchange ||
            item?.items?.some((orderItem) => orderItem?.is_exchange),
          situation: item?.situation,
          user: item?.user?.name || '-',
          created_at: item?.created_at || '-',
          isDeleted: !!item?.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
  })

  const { orders = [], totalPages = 1 } = ordersResponseData || {}

  const blockAction = useMemo(
    () => orders?.situation === OrderSituationEnum.CANCELLED,
    [orders]
  )

  const columns = [
    {
      key: 'id',
      label: 'Número',
      accessorKey: 'id',
      render: (value, order) => {
        const isExpanded = String(expandedOrderId) === String(order.id)

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}>
            <span>{value}</span>
            {!order.isDeleted && canAccess('Ver ordens de serviço') && (
              <IconButton
                size="small"
                color="primary"
                aria-label={
                  isExpanded
                    ? 'Recolher detalhes da requisição'
                    : 'Expandir detalhes da requisição'
                }
                onClick={() =>
                  setExpandedOrderId(isExpanded ? null : order.id)
                }
                sx={{ p: 0.25, mt: -0.5 }}>
                {isExpanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>
        )
      },
    },
    {
      key: 'contract_number',
      label: 'N° Contrato',
      accessorKey: 'contract_number',
      width: '8%',
    },
    { key: 'type', label: 'Tipo', accessorKey: 'type' },
    {
      key: 'contract',
      label: 'Contrato',
      accessorKey: 'contract',
      width: '25%',
    },
    {
      key: 'recipient_customer',
      label: 'Cliente destinatário',
      accessorKey: 'recipient_customer',
      width: '25%',
    },
    {
      key: 'destination_order',
      label: 'Destino',
      accessorKey: 'destination_order',
      width: '20%',
    },
    { key: 'statusOs', label: 'Status', accessorKey: 'statusOs' },
    {
      key: 'created_at',
      label: 'Criado Em',
      accessorKey: 'created_at',
      width: '8%',
    },
    {
      key: 'deadline',
      label: 'Prazo Final',
      accessorKey: 'deadline',
      width: '8%',
    },
    {
      key: 'situation',
      label: 'Situação',
      align: 'center',
      render: (value, order) => {
        if (value === OrderSituationEnum.APPROVED) {
          return (
            <Chip
              label={getOrderSituationLabel(value)}
              size="small"
              color="success"
              icon={<FontAwesomeIcon icon={faCheckCircle} />}
              sx={{ fontWeight: 'bold' }}
            />
          )
        }

        if (value === OrderSituationEnum.CANCELLED) {
          return (
            <Chip
              label={getOrderSituationLabel(value)}
              size="small"
              color="error"
              icon={<FontAwesomeIcon icon={faTimesCircle} />}
              sx={{ fontWeight: 'bold' }}
            />
          )
        }

        return (
          <Chip
            label={getOrderSituationLabel(OrderSituationEnum.PENDING)}
            size="small"
            color="warning"
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            variant="filled"
          />
        )
      },
    },
  ]

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoadingOrders || isFetchingOrders) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoadingOrders, isFetchingOrders, showLoader, hideLoader])

  // --- Notificação de Erro para a Query de Contratos ---
  useEffect(() => {
    if (isErrorOrders) {
      console.error(
        'OrdersServicesPage: Erro ao carregar requisições (useQuery):',
        ordersQueryError
      )
      showNotification(
        'error',
        `Erro ao carregar requisições: ${
          ordersQueryError.response?.data?.message || ordersQueryError.message
        }`
      )
    }
  }, [isErrorOrders, ordersQueryError, showNotification])

  const deleteOrderServiceMutation = useMutation({
    mutationFn: async (osIdToInactivate) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.serviceOrders.delete(osIdToInactivate)
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        invalidateByEvent(queryClient, 'ORDER_SERVICE_DELETE')
      }
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  // --- useEffect para mensagens de estado de localização ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, situation }) => {
      const response = await baseServicePut(
        entities.serviceOrders.updateStatus(id),
        {
          order: { situation },
        }
      )
      return response
    },
    onSuccess: (response) => {
      if (response.status === 200) {
        queryClient.invalidateQueries(['orders'])
        queryClient.invalidateQueries(['serviceOrderDetailsPanel'])
      }
    },
    onError: (error) => {
      showNotification(
        'error',
        error.response?.data?.message || 'Erro ao atualizar a situação.'
      )
    },
  })

  const actions = useMemo(
    () => [
      // TODO: Temporariamente desabilitado, em desenvolvimento
      // {
      //   id: "attach_contract",
      //   icon: faAtlas,
      //   title: "Atrelar a um contrato",
      //   buttonClass: {
      //     color: "#FF8F00",
      //     iconColor: "#FF8F00",
      //     bgColorHover: "#FFECB3",
      //   },
      //   permission: "Ver ordens de serviço",
      //   onClick: (orderService) =>
      //     navigate(`/aluguel/requisicoes/${orderService.id}/atrelar-contrato`),
      //   show: (orderService) =>
      //     !orderService.isDeleted &&
      //     orderService.type_id === MovementEnum.AVULSO &&
      //     canAccess("Ver ordens de serviço"),
      // },
      {
        id: 'approve',
        icon: faCheckCircle,
        title: 'Aprovar',
        buttonClass: 'btn-success',
        permission: 'Atualizar ordens de serviço',
        onClick: (order) =>
          showConfirmation(
            order,
            'Deseja aprovar esta requisição?',
            async () =>
              await updateStatusMutation.mutateAsync({
                id: order.id,
                situation: OrderSituationEnum.APPROVED,
              })
          ),
        show: (order) =>
          !order.isDeleted &&
          order.situation === OrderSituationEnum.PENDING &&
          (!order.has_exchange || canDecideExchange) &&
          canAccess('Atualizar ordens de serviço'),
      },

      {
        id: 'reject',
        icon: faTimesCircle,
        title: 'Rejeitar',
        buttonClass: 'btn-warning',
        permission: 'Atualizar ordens de serviço',
        onClick: (order) =>
          showConfirmation(
            order,
            'Deseja rejeitar esta requisição?',
            async () =>
              await updateStatusMutation.mutateAsync({
                id: order.id,
                situation: OrderSituationEnum.CANCELLED,
              })
          ),
        show: (order) =>
          !order.isDeleted &&
          order.situation === OrderSituationEnum.PENDING &&
          (!order.has_exchange || canDecideExchange) &&
          canAccess('Atualizar ordens de serviço'),
      },
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver ordens de serviço',
        onClick: (orderService) =>
          navigate(`/aluguel/requisicoes/${orderService.id}/detalhes`),
        show: (orderService) =>
          !orderService.isDeleted && canAccess('Ver ordens de serviço'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar ordens de serviço',
        onClick: (orderService) =>
          navigate(
            orderService?.contract_id
              ? `/aluguel/requisicoes/${orderService.id}/editar?contract_id=${orderService.contract_id}`
              : `/aluguel/requisicoes/${orderService.id}/editar`,
            {
              state: { from: location.pathname },
            }
          ),
        show: (orderService) =>
          !blockAction &&
          !orderService.isDeleted &&
          [GenericStatusEnum.NOT_ATTENDED].includes(orderService.status_id) &&
          canAccess('Atualizar ordens de serviço'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir ordens de serviço',
        onClick: (os) =>
          showConfirmation(
            os,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await deleteOrderServiceMutation.mutateAsync(os.id)
              } catch (error) {
                console.error('Erro ao excluir ordem de serviço:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (os) =>
          !blockAction &&
          !os.isDeleted &&
          [GenericStatusEnum.NOT_ATTENDED].includes(os.status_id) &&
          canAccess('Excluir ordens de serviço'),
      },
    ],
    [navigate, canAccess, canDecideExchange]
  )

  const [contractFilter, setContractFilter] = useState(null)
  const [recipientCustomerFilter, setRecipientCustomerFilter] = useState(null)

  const orderSituationOptions = [
    { value: 'all', label: 'Todos' },
    {
      value: OrderSituationEnum.PENDING,
      label: getOrderSituationLabel(OrderSituationEnum.PENDING),
    },
    {
      value: OrderSituationEnum.APPROVED,
      label: getOrderSituationLabel(OrderSituationEnum.APPROVED),
    },
    {
      value: OrderSituationEnum.CANCELLED,
      label: getOrderSituationLabel(OrderSituationEnum.CANCELLED),
    },
  ]

  const inputsfilters = useMemo(
    () =>
      [
        !contractId && {
          label: 'Contrato',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase={['number', 'name']}
              model="contract"
              value={localFilterValues['contract.number']}
              extraFilters={{
                organization_id: { $eq: selectedOrgan?.id },
              }}
              isCleabed
              onChange={(selected) => {
                const val = selected?.value
                handleFilterChange('contract.id', val ? { $eq: val } : null)
                setContractFilter(val ? val : null)
              }}
              placeholder="Filtre pelo número ou nome do contrato"
            />
          ),
        },
        {
          label: 'Cliente Destinatário',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase={['id', 'name']}
              extraFilters={{
                name: {
                  $nin: ['ALUCOM', 'IP', 'MOREIA', 'AS SISTEMAS'],
                },
                'contracts.id': { $eq: contractFilter },
              }}
              model="customer"
              value={localFilterValues['recipientCustomer.name']}
              onChange={(selected) => {
                const val = selected?.value
                handleFilterChange(
                  'recipientCustomer.id',
                  val ? { $eq: val } : null
                )
                setRecipientCustomerFilter(val ? val : null)
              }}
              placeholder="Filtre pelo número ou nome do contrato"
              refetchDependencies={[contractFilter]}
            />
          ),
        },
        {
          label: 'Destino',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase={[
                'id',
                'alias',
                'street',
                'number',
                'city',
                'state',
                'zip',
              ]}
              model="address"
              value={localFilterValues['destination_address.alias']}
              extraFilters={{
                ...(recipientCustomerFilter !== null && {
                  addressable_id: { $eq: recipientCustomerFilter },
                  addressable_type: { $eq: 'customer' },
                }),
              }}
              refetchDependencies={[recipientCustomerFilter]} // * Atualiza quando o cliente destinatário for alterado
              onChange={(selected) => {
                const val = selected?.value
                handleFilterChange(
                  'destination_address',
                  val ? { $eq: val } : null
                )
              }}
              placeholder="Filtre pelo número ou nome do contrato"
            />
          ),
        },
        {
          label: 'Status',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase="name"
              extraFilters={{ id: { $notin: [4, 5] } }}
              model="serviceOrderStatus"
              value={localFilterValues['status.name']}
              onChange={(selected) =>
                handleFilterChange('status.name', selected)
              }
              placeholder="Filtre pelo status"
            />
          ),
        },
        {
          label: 'Equipamento',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase={['number', 'name']}
              extraFilters={{
                is_completed: { $eq: 1 },
              }}
              model="equipment"
              value={localFilterValues['items.equipment.number']}
              valueKey="number"
              onChange={(selected) =>
                handleFilterChange('items.equipment.number', selected)
              }
              placeholder="Selecione ou digite o numero ou a descrição"
              isMulti
            />
          ),
        },
        {
          label: 'Situação da Requisição',
          component: (
            <InputSelect
              id="situation"
              label="Situação"
              value={localFilterValues.situation ?? 'all'}
              onChange={(e) => {
                const value = e.target.value

                handleFilterChange('situation', value === 'all' ? null : value)
              }}
              options={orderSituationOptions}
            />
          ),
        },
        {
          label: 'Tipo do movimento',
          component: (
            <AutoCompleteFilter
              service={baseService}
              columnDataBase="name"
              model="movementType"
              extraFilters={{ id: { $notin: [5, 6] } }}
              value={localFilterValues['movementType.name']}
              onChange={(selected) => {
                const val = selected?.value
                handleFilterChange('movementType.id', val ? { $eq: val } : null)
              }}
              placeholder="Filtre pelo tipo de movimento"
              isClearable
            />
          ),
        },
      ].filter(Boolean),
    [localFilterValues, handleFilterChange]
  )

  const isPageLoading = isLoadingOrders || isFetchingOrders
  // deleteOrderServiceMutation.isPending;

  useEffect(() => {
    if (isPageLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isPageLoading, showLoader, hideLoader])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Requisições"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Requisições"
          buttonText="Nova Requisição"
          buttonLink="/aluguel/requisicoes/criar"
          canAccess={canAccess}
          permission="Criar ordens de serviço"
        />

        <TableWithInactiveFilter
          data={orders}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: genericStatusColorMap,
            propertyKey: 'statusOs', // ou o campo que contém o ID do status
          }}
          isLoading={isPageLoading}
          isFetching={isFetchingOrders}
          expandedRowId={expandedOrderId}
          renderExpandedRow={(order) => (
            <ServiceOrderDetailsPanel orderId={order.id} />
          )}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            selectedItem
              ? `${selectedItem.contract_name} - ${selectedItem.statusOs}`
              : ''
          }
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default OrdersServicesPage
